import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthProvider';
import { AuthPage } from './components/auth/AuthPage';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { EmailConfirmedPage } from './components/auth/EmailConfirmedPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { HomePage } from './components/pages/HomePage';
import { ConversationFlow } from './components/conversation/ConversationFlow';
import { WatchlistPage } from './components/pages/WatchlistPage';
import { WatchedMoviesPage } from './components/pages/WatchedMoviesPage';
import { AccountPage } from './components/pages/AccountPage';
import { PrivacyPolicy } from './components/pages/PrivacyPolicy';
import { TermsOfService } from './components/pages/TermsOfService';
import { NativeAppBootstrap } from './components/NativeAppBootstrap';
import { NativeBottomNav } from './components/NativeBottomNav';
import { OfflineBanner } from './components/OfflineBanner';
import { usePageViewTracker } from './hooks/usePageViewTracker';
import { STARTUP_CONFIG_OK, STARTUP_ISSUES } from './lib/startupConfig';
import { ConfigErrorScreen } from './components/ConfigErrorScreen';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="grain flex min-h-screen items-center justify-center">
        <div className="animate-rise space-y-5 text-center">
          <span className="display text-3xl font-semibold tracking-tight">
            Movie<span className="text-primary">Me</span>
          </span>
          <div className="mx-auto h-1 w-40 overflow-hidden rounded-full bg-secondary">
            <div className="animate-progress h-full rounded-full bg-primary" />
          </div>
          <p className="text-sm text-muted-foreground">Dimming the lights…</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <AuthPage />;
}

function AppRoutes() {
  usePageViewTracker();

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversation"
        element={
          <ProtectedRoute>
            <ConversationFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <WatchlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/watched"
        element={
          <ProtectedRoute>
            <WatchedMoviesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

/**
 * Normal MovieMe shell: auth provider, native bootstrap, tabs, offline banner.
 * Confirmation/reset callbacks never mount inside this tree (see App).
 */
export function MovieMeApplication() {
  return (
    <AuthProvider>
      <NativeAppBootstrap />
      <AppRoutes />
      <OfflineBanner />
      <NativeBottomNav />
    </AuthProvider>
  );
}

function App() {
  if (!STARTUP_CONFIG_OK) {
    return (
      <ErrorBoundary>
        <ConfigErrorScreen issues={STARTUP_ISSUES} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<MovieMeApplication />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
